import React from 'react';
import { Link } from '@inertiajs/react';
import { FaInstagram, FaReddit, FaYoutube } from 'react-icons/fa';
import LinkExternal from '@/Components/LinkExternal.jsx';
import { FaXTwitter } from 'react-icons/fa6';

const MyFooter = ({ setIsFeedbackModalOpen }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 dark:bg-gray-800 py-10 mt-10">
      <div className="max-w-screen-xl px-4 mx-auto md:px-8 lg:px-10">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
          {/* Main Pages Section */}
          <div className="lg:mx-auto">
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">
              Pages
            </h2>
            <ul className="text-gray-500 dark:text-gray-400">
              <li className="mb-4">
                <Link href="/" className="no-underline">
                  Home
                </Link>
              </li>
              <li className="mb-4">
                <Link href={route('membership')} className="no-underline">
                  Pricing
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/reviews" className="no-underline">
                  Reviews
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/contact" className="no-underline">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Section */}
          <div className="lg:mx-auto">
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">
              Resources
            </h2>
            <ul className="text-gray-500 dark:text-gray-400">
              <li className="mb-4">
                <LinkExternal href="https://sheetsresume.com/faq/" className="no-underline">
                  FAQ
                </LinkExternal>
              </li>
              <li className="mb-4">
                <LinkExternal
                  href="https://sheetsresume.com/resume-template/"
                  className="no-underline"
                >
                  Free Resume Template
                </LinkExternal>
              </li>
              <li className="mb-4">
                <LinkExternal href="https://sheetsresume.com/advice/" className="no-underline">
                  Advice
                </LinkExternal>
              </li>
              <li className="mb-4">
                <LinkExternal href="https://sheetsresume.com/about/" className="no-underline">
                  About
                </LinkExternal>
              </li>
            </ul>
          </div>

          {/* AI Job Tools Section */}
          <div className="lg:mx-auto">
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">
              AI Job Tools
            </h2>
            <ul className="text-gray-500 dark:text-gray-400">
              <li className="mb-4">
                <Link href="/builder" className="no-underline">
                  AI Resume Builder
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/cover-letter" className="no-underline">
                  Free Cover Letter Maker
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/mock-interview" className="no-underline">
                  AI Mock Interviews
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/jobs" className="no-underline">
                  AI Job Board
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Section */}
          <div className="lg:mx-auto">
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">
              Policies
            </h2>
            <ul className="text-gray-500 dark:text-gray-400">
              <li className="mb-4">
                <LinkExternal href="https://sheetsresume.com/privacy/" className="no-underline">
                  Privacy Policy
                </LinkExternal>
              </li>
              <li className="mb-4">
                <LinkExternal href="https://sheetsresume.com/terms" className="no-underline">
                  Terms & Conditions
                </LinkExternal>
              </li>
              <li className="mb-4">
                <Link href="/start/referral" className="no-underline">
                  Referral Program
                </Link>
              </li>
              <li className="mb-4">
                <Link href="/start/organization" className="no-underline">
                  For Organizations
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Media Icons Row */}
        <div className="flex justify-center items-center space-x-6 mt-8">
          <LinkExternal
            target="_new"
            href="http://www.youtube.com/@SheetsResume"
            className="text-gray-500 hover:text-red-600 dark:hover:text-white"
            aria-label="YouTube"
          >
            <FaYoutube className="text-red-600 w-8 h-8" />
          </LinkExternal>
          <LinkExternal
            target="_new"
            href="https://www.reddit.com/r/SheetsResume/"
            className="text-gray-500 hover:text-orange-500 dark:hover:text-white"
            aria-label="Reddit"
          >
            <FaReddit className="text-orange-500 w-8 h-8" />
          </LinkExternal>
          <LinkExternal
            target="_new"
            href="https://x.com/sheetsresume"
            className="text-gray-500 hover:text-black dark:hover:text-white"
            aria-label="X/Twitter"
          >
            <FaXTwitter className="text-white bg-black w-8 h-8" />
          </LinkExternal>
          <LinkExternal
            target="_new"
            href="https://instagram.com/sheetsresume"
            className="text-gray-500 hover:text-pink-500 dark:hover:text-white"
            aria-label="Instagram"
          >
            <FaInstagram className="text-pink-500 w-8 h-8" />
          </LinkExternal>
        </div>

        {/* Feedback Section */}
        <div className="flex items-center justify-center mt-10">
          <div className="items-center">
            <button
              className="flex m-auto items-center text-white bg-yellow-500 hover:bg-yellow-700 focus:ring-4 focus:ring-yellow-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-4 dark:bg-yellow-600 dark:hover:bg-yellow-700 focus:outline-none dark:focus:ring-yellow-800"
              id="temporary-feedback-button-id"
              onClick={() => setIsFeedbackModalOpen(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
                />
              </svg>
              Problems, comments or suggestions?
            </button>
            <p className="text-center text-gray-500 text-sm">
              <span className="italic">
                See an issue? Feel like sending some love? Please let us know! We read all of these.
              </span>
              &nbsp;🙂
            </p>
          </div>
        </div>

        <hr className="my-6 border-gray-200 dark:border-gray-700 lg:my-8" />

        {/* Stripe Climate Badge */}
        <div className="flex flex-col justify-center items-center mb-6">
          <iframe
            width="380"
            height="82"
            style={{ border: 0 }}
            src="https://climate.stripe.com/badge/RA58fS?theme=dark&size=large&locale=en-US"
            title="Stripe Climate Badge"
          ></iframe>
          <LinkExternal
            target="_new"
            href="https://climate.stripe.com/VY1Ka6"
            className="mt-2 text-sm text-gray-600 hover:text-green-700 dark:text-gray-400 dark:hover:text-green-400"
          >
            Learn more about our Climate Pledge
          </LinkExternal>
        </div>

        <span className="block mt-4 text-sm text-center text-gray-500 dark:text-gray-400">
          © {currentYear}{' '}
          <LinkExternal href="https://sheetsresume.com" className="no-underline">
            SheetsResume.com
          </LinkExternal>
          . All Rights Reserved.
        </span>
      </div>
    </footer>
  );
};

export default MyFooter;
